import { useEffect, useState } from "react";
import { X, Trophy } from "lucide-react";
import { api } from "@/lib/api";

const KIND_STYLE = {
  grade:  "from-emerald-50 to-sky-50 border-emerald-300 text-emerald-900",
  score:  "from-sky-50 to-indigo-50 border-sky-300 text-sky-900",
  metric: "from-amber-50 to-orange-50 border-amber-300 text-amber-900",
};

/** Milestone banner that celebrates week-over-week grade transitions,
 *  weighted-score jumps ≥ 0.5 pts, and per-metric coverage wins ≥ 3 pp.
 *  Renders at the very top of the Progress Dash.  Dismissible; hides
 *  the same milestone for 7 days after dismissal. */
export default function MilestoneBanner() {
  const [items, setItems]   = useState([]);
  const [ready, setReady]   = useState(false);
  const [busy, setBusy]     = useState(null); // key currently being dismissed

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/progress/milestones");
        if (alive) setItems(r.data?.milestones || []);
      } catch {
        /* silent — banner is decorative */
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = async (key) => {
    setBusy(key);
    try {
      await api.post("/admin/progress/dismiss-milestone", { key });
      setItems((xs) => xs.filter((x) => x.key !== key));
    } finally {
      setBusy(null);
    }
  };

  if (!ready || !items.length) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="milestone-banner">
      {items.map((m) => {
        const style = KIND_STYLE[m.kind] || KIND_STYLE.grade;
        return (
          <div
            key={m.key}
            data-testid={`milestone-${m.kind}`}
            className={`relative rounded-xl border bg-gradient-to-r ${style} px-5 py-3 shadow-sm flex items-start gap-3`}
          >
            <div className="mt-0.5 shrink-0" aria-hidden>
              {m.kind === "grade" ? (
                <Trophy className="h-5 w-5" />
              ) : (
                <span className="text-lg leading-none">{m.emoji || "🎉"}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold leading-snug">
                {m.emoji && m.kind !== "grade" ? "" : "🎉 "}{m.headline}
              </div>
              {m.detail && (
                <div className="mt-0.5 text-[12px] opacity-80 leading-snug">
                  {m.detail}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(m.key)}
              disabled={busy === m.key}
              className="shrink-0 p-1 rounded hover:bg-white/60 transition-colors disabled:opacity-50"
              aria-label="Dismiss milestone"
              data-testid={`milestone-dismiss-${m.kind}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
